import { html, TemplateResult, PropertyValues } from 'lit';
import { property, customElement, query } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { DaySchedule, GzSchedulerPermission, Interval, SchedulerValue } from '../types';
import { translateFn } from '../../../utils/translator.helper';
import { classMap } from 'lit/directives/class-map.js';
import { GzRadioGroupWebComponent } from '../../gzRadio';
import { FormFieldInput } from '../../gzForm/formField/component/FormFieldInput';

/**
 * @summary GzSchedulerWebComponent: A LitElement-based scheduler component with toggleable day and hour intervals.
 * @tag gz-scheduler
 * @event change - fires when one or more intervals are toggled.
 * @property {DaySchedule[]} schedule - The schedule data for the component. Each element is a DaySchedule object with the day index (0-6) and intervals.
 * @cssproperty --table-border-color
 * @cssproperty --table-text-color
 * @cssproperty --table-cell-color-default
 * @cssproperty --table-cell-color-selected
 * @cssproperty --table-cell-color-hovered
 */
@customElement('gz-scheduler')
export class GzSchedulerWebComponent extends FormFieldInput<SchedulerValue> {
  /**
   * Schedule data for the component. Each element is a DaySchedule object with the day index (0-6) and intervals.
   */
  @property({ type: Array, reflect: true, attribute: true })
  schedule: DaySchedule[] = [];

  /**
   * UI representation of the schedule. Each element is a boolean array of 24 elements (hours).
   * If the hour is selected, the value is true, otherwise false.
   * @private
   */
  @property({ type: Array })
  private timeSlots: boolean[][] = [];

  @property({ type: String, reflect: true, attribute: true })
  label: string | null = null;

  @query('#radioGroup')
  private radioGroup!: GzRadioGroupWebComponent;

  /**
   * Map of day names
   * @private
   */
  private dayNames = new Map<number, string>([
    [0, 'Sun'],
    [1, 'Mon'],
    [2, 'Tue'],
    [3, 'Wed'],
    [4, 'Thu'],
    [5, 'Fri'],
    [6, 'Sat'],
  ]);

  private get schedulerPermission(): GzSchedulerPermission {
    const radioSelection = this.radioGroup?.selectedOption as GzSchedulerPermission;
    return radioSelection || GzSchedulerPermission.CUSTOM;
  }

  public getFormValue(): SchedulerValue {
    const convertedSchedule = this.convertTimeSlotsIntoDaySchedule();
    return {
      schedule: convertedSchedule,
      permission: this.schedulerPermission,
    };
  }

  public setFormValue(value: SchedulerValue | null): void {
    if (value && value.schedule) {
      this.schedule = [...value.schedule];
    } else {
      this.schedule = [];
    }
    this.dispatchEvent(new CustomEvent('change'));
  }

  static get styles() {
    return styles;
  }

  protected updated(changedProperties: PropertyValues) {
    if (changedProperties.has('schedule')) {
      this.timeSlots = this.convertDayScheduleIntoTimeSlots();
      this.dispatchEvent(new Event('slotchange', { bubbles: true, composed: true }));
      this.updateRadioSelection();
      this.dispatchEvent(new CustomEvent('change'));
    }
  }

  /**
   * Converts the time slots into a DaySchedule array.
   * @returns DaySchedule[]
   */
  public convertTimeSlotsIntoDaySchedule(): DaySchedule[] {
    return this.timeSlots.map((daySlots, day) => {
      const { intervals, lastStart } = daySlots.reduce(
        (acc, slot, hour) => {
          if (slot && acc.lastStart === null) {
            // Start of a new interval
            return { ...acc, lastStart: hour };
          } else if (!slot && acc.lastStart !== null) {
            // End the current interval
            acc.intervals.push({ start: acc.lastStart, end: hour - 1 });
            return { ...acc, lastStart: null };
          }
          return acc; // Continue accumulating without changes
        },
        { intervals: [] as Interval[], lastStart: null as number | null }
      );

      // If an interval is still open at the end of the day, close it
      if (lastStart !== null) {
        intervals.push({ start: lastStart, end: 23 });
      }

      return { day, intervals };
    });
  }

  /**
   * Converts the DaySchedule array into the timeSlots array.
   * This is used internally for rendering the scheduler table.
   * @returns boolean[][]
   */
  private convertDayScheduleIntoTimeSlots(): boolean[][] {
    // Initialize the timeSlots grid with 7 days and 24 hours per day, all set to false
    const timeSlots = Array.from({ length: 7 }, () => Array(24).fill(false));

    // Process each day in the schedule
    for (const { day, intervals } of this.schedule) {
      // Validate day is within the expected range (0-6)
      if (day < 0 || day > 6) {
        console.error(`Invalid day index: ${day}`);
        continue;
      }

      // Process each interval for the day
      for (const { start, end } of intervals) {
        // Ensure start and end are within the valid hour range (0-23)
        const validStart = Math.max(0, start);
        const validEnd = Math.min(23, end);

        // Mark each time slot from validStart to validEnd as occupied
        for (let t = validStart; t <= validEnd; t++) {
          timeSlots[day][t] = true;
        }
      }
    }

    return timeSlots;
  }

  private toggleHour(day: number, hour: number): void {
    const timeSlot = [...this.timeSlots[day]];

    timeSlot[hour] = !this.timeSlots[day][hour];
    this.timeSlots = [...this.timeSlots.slice(0, day), timeSlot, ...this.timeSlots.slice(day + 1)];
    this.dispatchEvent(new CustomEvent('change'));
    this.updateRadioSelection();
  }

  /**
   * Select all intervals for all days
   */
  public selectAllIntervals(): void {
    this.timeSlots = this.timeSlots.map(daySlots => daySlots.map(() => true));
    this.dispatchEvent(new CustomEvent('change'));
  }

  /**
   * Deselect all intervals for all days
   */
  public deselectAllIntervals(): void {
    this.timeSlots = this.timeSlots.map(daySlots => daySlots.map(() => false));
    this.dispatchEvent(new CustomEvent('change'));
  }

  /**
   * Toggle intervals for a specific hour across all days (column selection).
   * If some days don't have this hour selected, select it for all days.
   * If all days have this hour selected, deselect it for all days.
   * @param day
   */
  private toggleColumn(day: number): void {
    const column = this.timeSlots[day];
    const allSelected = column.every(interval => interval);

    this.timeSlots = this.timeSlots.map((d, index) => {
      if (index === day) {
        return d.map(() => !allSelected);
      }
      return d;
    });
    this.dispatchEvent(new CustomEvent('change'));
    this.updateRadioSelection();
  }

  /**
   * Toggle intervals for a specific day (row selection).
   * If some intervals for the day are not selected, select them.
   * If all intervals are selected, deselect the entire day.
   * @param hour
   */
  private toggleRow(hour: number): void {
    const allSelected = this.timeSlots.every(daySlots => daySlots[hour]);

    this.timeSlots = this.timeSlots.map(daySlots => {
      daySlots[hour] = !allSelected;
      return daySlots;
    });
    this.dispatchEvent(new CustomEvent('change'));
    this.updateRadioSelection();
  }

  private updateRadioSelection(): void {
    const allSelected = this.timeSlots.length > 0 && this.timeSlots.every(daySlots => daySlots.every(slot => slot));
    const allDeselected =
      this.timeSlots.length === 0 || this.timeSlots.every(daySlots => daySlots.every(slot => !slot));

    if (allDeselected) {
      this.radioGroup.setFormValue(GzSchedulerPermission.ALLOW_ALL);
    } else if (allSelected) {
      this.radioGroup.setFormValue(GzSchedulerPermission.BLOCK_ALL);
    } else {
      this.radioGroup.selectedOption = '';
    }
  }

  private renderTableCell(hour: number): TemplateResult {
    return html`${this.timeSlots.map((daySlots, day) => {
      const classes = {
        selected: daySlots[hour],
        'time-slot': true,
      };
      return html`
        <td>
          <input
            class="${classMap(classes)}"
            @change="${() => this.toggleHour(day, hour)}"
            type="checkbox"
            id="checkbox-${day}-${hour}"
          />
          <label class="table-cell-label" for="checkbox-${day}-${hour}"></label>
        </td>
      `;
    })}`;
  }

  private renderTable(): TemplateResult {
    return html`
      <table>
        <thead>
          <tr>
            <th></th>
            ${this.timeSlots.map(
              (day, index) =>
                html`<th class="day-selector" id="${day}" @click="${() => this.toggleColumn(index)}">
                  ${translateFn(this.dayNames.get(index) as string)}
                </th>`
            )}
          </tr>
        </thead>
        <tbody>
          ${Array.from(
            { length: 24 },
            (_, hour) => html`
              <tr>
                <td @click="${() => this.toggleRow(hour)}" class="hour-selector">
                  ${hour.toString().padStart(2, '0')}:00
                </td>
                ${this.renderTableCell(hour)}
              </tr>
            `
          )}
        </tbody>
      </table>
    `;
  }

  private onRadioSelectionChanged(e: CustomEvent): void {
    const { selectedValue } = e.detail;
    if (selectedValue === GzSchedulerPermission.ALLOW_ALL) {
      this.deselectAllIntervals();
    } else {
      this.selectAllIntervals();
    }
  }

  private renderSelectControls(): TemplateResult {
    return html`
      <div class="wrapper">
        <label class="form-label">${this.label}</label>
        <gz-radio-group id="radioGroup" class="radio-group" @radioSelectionChanged="${this.onRadioSelectionChanged}">
          <div class="radio-option">
            <gz-radio label="${translateFn('Allow all')}" .value="${GzSchedulerPermission.ALLOW_ALL}"></gz-radio>
            <span class="swatch default"></span>
          </div>
          <div class="radio-option">
            <gz-radio label="${translateFn('Block all')}" .value="${GzSchedulerPermission.BLOCK_ALL}"></gz-radio>
            <span class="swatch block"></span>
          </div>
        </gz-radio-group>
      </div>
    `;
  }

  private renderInvalidTemplate(): undefined | TemplateResult {
    if (this.invalid) {
      return html` <gz-info-box severity="error">${this.errorTooltipText}</gz-info-box>`;
    }
  }

  render() {
    return html` ${this.renderSelectControls()} ${this.renderInvalidTemplate()} ${this.renderTable()} `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'gz-scheduler': GzSchedulerWebComponent;
  }
}
